import {
  Box,
  Divider,
  Flex,
  Form,
  MonetaryInputField,
  SubmitButton,
  Text,
  TextAreaInputField,
} from '@/components';
import { KsAdditionalAttributesExecutionDivergencesFormSchema } from '@/features/project/projects/_models/project';
import { useForm } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<
  typeof KsAdditionalAttributesExecutionDivergencesFormSchema
>;

type Props = {
  hasOneYearsAgo: boolean;
  hasTwoYearsAgo: boolean;
  hasTwoYearsAgoProject: boolean;
  hasOneYearsAgoProject: boolean;
  fiscalYear: number;
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const UpdateDivergenceForm: FC<Props> = ({
  hasOneYearsAgo,
  hasOneYearsAgoProject,
  hasTwoYearsAgo,
  hasTwoYearsAgoProject,
  fiscalYear,
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(KsAdditionalAttributesExecutionDivergencesFormSchema),
  });

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <Flex vertical gap={16}>
        <Text variant="h5" text={t('situation-of-divergence-in-execution')} />
        <Box>
          {!hasTwoYearsAgoProject && (
            <MonetaryInputField
              name="executionDivergences.1.estimatedBusinessExpense"
              label={t('estimated-business-expenses-with-year', {
                year: fiscalYear - 2,
              })}
              control={control}
              errors={errors}
              unit={unit}
            />
          )}

          {!hasTwoYearsAgo && (
            <MonetaryInputField
              name="executionDivergences.1.actualBusinessExpense"
              label={t('business-expenses-with-year', {
                year: fiscalYear - 2,
              })}
              control={control}
              errors={errors}
              unit={unit}
            />
          )}

          <TextAreaInputField
            name="executionDivergences.1.reason"
            label={t('reason-for-divergence-two-year-ago')}
            control={control}
            errors={errors}
            rows={3}
          />

          <Divider />

          {!hasOneYearsAgoProject && (
            <MonetaryInputField
              name="executionDivergences.0.estimatedBusinessExpense"
              label={t('estimated-business-expenses-with-year', {
                year: fiscalYear - 1,
              })}
              control={control}
              errors={errors}
              unit={unit}
            />
          )}
          {!hasOneYearsAgo && (
            <MonetaryInputField
              name="executionDivergences.0.actualBusinessExpense"
              label={t('business-expenses-with-year', {
                year: fiscalYear - 1,
              })}
              control={control}
              errors={errors}
              unit={unit}
            />
          )}

          <TextAreaInputField
            name="executionDivergences.0.reason"
            label={t('reason-for-divergence-one-year-ago')}
            control={control}
            errors={errors}
            rows={3}
          />
        </Box>
      </Flex>

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
